// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {OutlineFrame} from '@app/features/app/components/layout/OutlineFrame';
import Authentication from '@app/features/auth/state/Authentication';
import styles from '@app/features/channel/components/ChannelMembers.module.css';
import {MemberListContainer} from '@app/features/channel/components/MemberListContainer';
import {MemberListGroupHeader} from '@app/features/channel/components/MemberListGroupHeader';
import {MemberListItem} from '@app/features/channel/components/MemberListItem';
import {
	MEMBER_LIST_GROUP_HEADER_HEIGHT_PX,
	MEMBER_LIST_ITEM_HEIGHT_PX,
	MEMBER_LIST_METRICS_STYLE,
	MEMBER_LIST_SCROLL_CHUNK_PX,
} from '@app/features/channel/components/MemberListMetrics';
import {MemberListSkeleton, MemberListSkeletonVariant} from '@app/features/channel/components/MemberListSkeleton';
import type {Channel} from '@app/features/channel/models/Channel';
import type {Guild} from '@app/features/guild/models/Guild';
import type {GuildRole} from '@app/features/guild/models/GuildRole';
import Guilds from '@app/features/guild/state/Guilds';
import {OFFLINE_DESCRIPTOR, ONLINE_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {
	buildMemberListLayout,
	buildMemberListRowOffsets,
	findScrollableParent,
	getGroupLayoutForRow,
	getTotalRowsFromLayout,
	type MemberListScrollSpan,
	quantizeMemberListScrollSpan,
} from '@app/features/member/utils/MemberListLayout';
import {buildMemberListRenderWindow} from '@app/features/member/utils/MemberListRangeUtils';
import {observeResize} from '@app/features/platform/utils/SharedResizeObserver';
import LocalPresence from '@app/features/presence/state/LocalPresence';
import ThreadRoster, {type ThreadRosterMember} from '@app/features/threads/state/ThreadRoster';
import {getAppRemScale} from '@app/features/ui/utils/AppZoomUtils';
import type {User} from '@app/features/user/models/User';
import type {CustomStatus} from '@app/features/user/state/CustomStatus';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {isOfflineStatus, type StatusType} from '@fluxer/constants/src/StatusConstants';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type {ReactNode} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';

const INITIAL_RENDER_SPAN_ROWS = 64;

export function useThreadRosterSubscription(thread: Channel | undefined): void {
	const guildId = thread?.guildId;
	const threadId = thread?.id;
	useEffect(() => {
		if (!guildId || !threadId) return;
		return ThreadRoster.subscribe(guildId, threadId);
	}, [guildId, threadId]);
}

interface RosterRow {
	user: User;
	member: GuildMember | undefined;
	status: StatusType;
	customStatus: CustomStatus | null;
}

interface RosterGroup {
	id: string;
	guildRole: GuildRole | null;
	label: string;
	members: ReadonlyArray<RosterRow>;
}

interface RosterWindowProps {
	groups: ReadonlyArray<RosterGroup>;
	thread: Channel;
	ownerId: string | null;
}

const RosterWindow = observer(({groups, thread, ownerId}: RosterWindowProps) => {
	const listRef = useRef<HTMLDivElement>(null);
	const [renderSpan, setRenderSpan] = useState<MemberListScrollSpan | null>(null);
	const zoomLevel = Accessibility.zoomLevel;
	const remScale = getAppRemScale();
	const memberHeight = MEMBER_LIST_ITEM_HEIGHT_PX * remScale;
	const headerHeight = MEMBER_LIST_GROUP_HEADER_HEIGHT_PX * remScale;
	const scrollChunk = MEMBER_LIST_SCROLL_CHUNK_PX * remScale;
	const layouts = useMemo(
		() => buildMemberListLayout(groups.map(({id, members}) => ({id, count: members.length}))),
		[groups],
	);
	const totalRows = getTotalRowsFromLayout(layouts);
	const rowOffsets = useMemo(
		() => buildMemberListRowOffsets(layouts, totalRows, {memberHeight, headerHeight}),
		[layouts, totalRows, memberHeight, headerHeight, zoomLevel],
	);
	useEffect(() => {
		const list = listRef.current;
		const scrollParent = findScrollableParent(list);
		if (!list || !scrollParent) return;
		let frame: number | null = null;
		const update = () => {
			frame = null;
			const scrollTop = scrollParent.getBoundingClientRect().top - list.getBoundingClientRect().top;
			const next = quantizeMemberListScrollSpan(scrollTop, scrollParent.clientHeight, scrollChunk);
			setRenderSpan((current) => (current?.top === next.top && current.height === next.height ? current : next));
		};
		const schedule = () => {
			if (frame == null) frame = window.requestAnimationFrame(update);
		};
		update();
		scrollParent.addEventListener('scroll', schedule, {passive: true});
		const stopObserving = typeof ResizeObserver === 'undefined' ? undefined : observeResize(scrollParent, schedule);
		return () => {
			scrollParent.removeEventListener('scroll', schedule);
			stopObserving?.();
			if (frame != null) window.cancelAnimationFrame(frame);
		};
	}, [scrollChunk, totalRows]);
	const [[firstRow, lastRow] = [0, -1]] =
		renderSpan == null
			? [[0, Math.min(INITIAL_RENDER_SPAN_ROWS, totalRows - 1)]]
			: buildMemberListRenderWindow({
					scrollTop: renderSpan.top,
					clientHeight: renderSpan.height,
					rowHeight: memberHeight,
					rowOffsets,
					bufferRows: 0,
					totalRows,
				});
	const rows: Array<ReactNode> = [];
	for (let rowIndex = firstRow; rowIndex <= lastRow; rowIndex += 1) {
		const layout = getGroupLayoutForRow(layouts, rowIndex);
		const group = groups.find((candidate) => candidate.id === layout?.id);
		if (!layout || !group) continue;
		const rowStyle = {transform: `translateY(${rowOffsets[rowIndex]}px)`};
		if (rowIndex === layout.headerRowIndex) {
			rows.push(
				<MemberListGroupHeader
					key={`group-${group.id}`}
					groupId={group.id}
					guildRole={group.guildRole}
					name={group.label}
					count={group.members.length}
					className={clsx(styles.virtualRow, styles.virtualGroupRow)}
					style={rowStyle}
				/>,
			);
			continue;
		}
		const {user, member, status, customStatus} = group.members[rowIndex - layout.headerRowIndex - 1]!;
		rows.push(
			<div
				key={`member-${user.id}`}
				className={clsx(styles.virtualRow, styles.virtualMemberRow)}
				style={rowStyle}
				data-flx="threads.thread-members-panel.roster-window.virtual-row.member"
			>
				<MemberListItem
					user={user}
					channelId={thread.id}
					guildId={thread.guildId}
					guildMember={member}
					roleColor={member?.getColorString()}
					status={status}
					customStatus={customStatus}
					isOwner={user.id === ownerId}
					disableBackdrop={true}
					data-flx="threads.thread-members-panel.roster-window.member-list-item"
				/>
			</div>,
		);
	}
	return (
		<div
			ref={listRef}
			className={styles.virtualListContent}
			style={{...MEMBER_LIST_METRICS_STYLE, height: `${rowOffsets[totalRows]}px`, flexShrink: 0}}
			data-flx="threads.thread-members-panel.roster-window.virtual-list-content"
		>
			{rows}
		</div>
	);
});

function resolveMembers(guildId: string | undefined, roster: ReadonlyArray<ThreadRosterMember>): Array<RosterRow> {
	const members: Array<RosterRow> = [];
	for (const entry of roster) {
		const user = Users.getUser(entry.userId);
		if (!user) continue;
		const member = guildId ? (GuildMembers.getMember(guildId, entry.userId) ?? undefined) : undefined;
		if (user.id === Authentication.currentUserId) {
			const status = LocalPresence.getStatus();
			members.push({user, member, status, customStatus: isOfflineStatus(status) ? null : LocalPresence.customStatus});
		} else {
			members.push({user, member, status: entry.status, customStatus: entry.customStatus});
		}
	}
	members.sort((a, b) =>
		NicknameUtils.getNickname(a.user, guildId).localeCompare(NicknameUtils.getNickname(b.user, guildId)),
	);
	return members;
}

function getHoistedRoles(guild: Guild | undefined): Array<GuildRole> {
	if (!guild) return [];
	return Object.values(guild.roles)
		.filter((role) => role.hoist && role.id !== guild.id)
		.sort((a, b) => b.effectiveHoistPosition - a.effectiveHoistPosition || (BigInt(a.id) < BigInt(b.id) ? -1 : 1));
}

export const ThreadMembersList = observer(({thread}: {thread: Channel}) => {
	const {i18n} = useLingui();
	useThreadRosterSubscription(thread);
	const roster = ThreadRoster.getMembers(thread.id);
	if (!roster) {
		return (
			<MemberListSkeleton
				variant={MemberListSkeletonVariant.GUILD}
				data-flx="threads.thread-members-panel.thread-members-list.member-list-skeleton"
			/>
		);
	}
	const guild = thread.guildId ? Guilds.getGuild(thread.guildId) : undefined;
	const ownerId = guild?.ownerId ?? null;
	const hoistedRoles = getHoistedRoles(guild);
	const roleGroups = new Map<string, Array<RosterRow>>(hoistedRoles.map((role) => [role.id, []]));
	const online: Array<RosterRow> = [];
	const offline: Array<RosterRow> = [];
	for (const row of resolveMembers(thread.guildId, roster)) {
		if (isOfflineStatus(row.status)) {
			offline.push(row);
			continue;
		}
		const role = hoistedRoles.find((candidate) => row.member?.roles.has(candidate.id));
		(role ? roleGroups.get(role.id)! : online).push(row);
	}
	const groups: Array<RosterGroup> = [
		...hoistedRoles.map((role) => ({
			id: role.id,
			guildRole: role,
			label: role.name,
			members: roleGroups.get(role.id)!,
		})),
		{id: 'online', guildRole: null, label: i18n._(ONLINE_DESCRIPTOR), members: online},
		{id: 'offline', guildRole: null, label: i18n._(OFFLINE_DESCRIPTOR), members: offline},
	];
	return (
		<RosterWindow
			groups={groups}
			thread={thread}
			ownerId={ownerId}
			data-flx="threads.thread-members-panel.thread-members-list.roster-window"
		/>
	);
});

export const ThreadMembersPanel = observer(({thread}: {thread: Channel}) => (
	<OutlineFrame hideTopBorder sides={{left: false}} data-flx="threads.thread-members-panel.outline-frame">
		<MemberListContainer channelId={thread.id} data-flx="threads.thread-members-panel.member-list-container">
			<ThreadMembersList thread={thread} data-flx="threads.thread-members-panel.thread-members-list" />
		</MemberListContainer>
	</OutlineFrame>
));
